import { useMemo, useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getServicePageUI } from '../../Public/service-page-data';
import ImageComponent from '../../Utils/Image/image-component';

/**
 * TypesSection - A generic, self-contained horizontal scroll section.
 * Handles titles, subtitles, and a collection of cards with icons and text.
 */
const TypesSection = ({ data }) => {
    const scrollRef = useRef(null);
    const [barWidth, setBarWidth] = useState(0);
    const [barOffset, setBarOffset] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [startScrollLeft, setStartScrollLeft] = useState(0);

    const systemData = useSelector((s) => s.systemData);
    const { language = 'en', dir = 'ltr' } = systemData || {};

    const uiContent = getServicePageUI(language).typesSection;

    // 1. Data Resolver: Support for direct data or language-keyed data
    const resolvedData = useMemo(() => {
        if (!data) return null;
        if (data[language]) return data[language];
        return data;
    }, [data, language]);

    // 2. Item Processor: Standardizes items for the UI
    const items = useMemo(() => {
        const list = resolvedData?.types || resolvedData?.items || [];
        if (!Array.isArray(list)) return [];

        return list.map((item, idx) => ({
            ...item,
            id: item.id || `item-${idx}`,
            paragraphs: item.text ? [item.text] : (Array.isArray(item.paragraphs) ? item.paragraphs : [])
        }));
    }, [resolvedData]);

    const updateProgressBar = () => {
        if (!scrollRef.current) return;
        const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
        if (scrollWidth === 0) return;

        const widthPercentage = (clientWidth / scrollWidth) * 100;
        setBarWidth(widthPercentage);

        const maxScroll = scrollWidth - clientWidth;
        const currentScroll = Math.abs(scrollLeft);
        const scrollRatio = maxScroll > 0 ? currentScroll / maxScroll : 0;
        const availableTrack = 100 - widthPercentage;
        setBarOffset(scrollRatio * availableTrack);
    };

    useEffect(() => {
        const container = scrollRef.current;
        if (container) {
            container.addEventListener('scroll', updateProgressBar);
            window.addEventListener('resize', updateProgressBar);
            updateProgressBar();
        }
        return () => {
            container?.removeEventListener('scroll', updateProgressBar);
            window.removeEventListener('resize', updateProgressBar);
        };
    }, [items]);

    const handleMouseDown = (e) => {
        const container = scrollRef.current;
        if (!container) return;
        setIsDragging(true);
        setStartX(e.pageX - container.offsetLeft);
        setStartScrollLeft(container.scrollLeft);
    };

    const handleStopDrag = () => setIsDragging(false);

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        e.preventDefault();
        const container = scrollRef.current;
        if (!container) return;
        const x = e.pageX - container.offsetLeft;
        const walk = (x - startX) * 1.5;
        container.scrollLeft = startScrollLeft - walk;
    };

    const handleScroll = (direction) => {
        const container = scrollRef.current;
        if (!container) return;
        const scrollAmount = container.clientWidth * 0.6;
        let delta = direction === 'next' ? scrollAmount : -scrollAmount;
        if (dir === 'rtl') delta = -delta;
        container.scrollBy({ left: delta, behavior: 'smooth' });
    };

    if (items.length === 0) return null;

    return (
        <section
            dir={dir}
            className="w-full bg-primary-50 dark:bg-black py-20 lg:py-32 font-assistant overflow-hidden relative"
        >
            {/* Engineering Grid Decoration */}
            <div className="absolute inset-0 opacity-5 pointer-events-none bg-[radial-gradient(#003366_1px,transparent_1px)] [background-size:40px_40px]"></div>

            <div className="max-w-8xl mx-auto px-0 lg:px-8 relative z-10">
                {/* --- Header Area --- */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-8 px-6 lg:px-2">
                    <div className="max-w-3xl">
                        <h2 className="text-5xl md:text-6xl lg:text-7xl font-black text-primary-900 dark:text-white mb-6 er leading-[0.95] uppercase">
                            {resolvedData?.title}
                        </h2>
                        <p className="text-xl md:text-2xl text-primary-800/70 dark:text-gray-400 font-medium max-w-2xl leading-relaxed">
                            {resolvedData?.subtitle}
                        </p>
                    </div>

                    <div className="hidden md:flex gap-4">
                        <button
                            onClick={() => handleScroll('prev')}
                            className="group w-16 h-16 rounded-full border-2 border-primary-900 bg-white flex items-center justify-center transition-all duration-300 hover:bg-primary-900 shadow-lg"
                        >
                            <span className="text-3xl text-primary-900 group-hover:text-white rtl:rotate-180">&#8592;</span>
                        </button>
                        <button
                            onClick={() => handleScroll('next')}
                            className="group w-16 h-16 rounded-full bg-secondary-500 text-white flex items-center justify-center transition-all duration-300 hover:scale-105 shadow-xl"
                        >
                            <span className="text-3xl font-black rtl:rotate-180">&#8594;</span>
                        </button>
                    </div>
                </div>

                {/* --- Scroll Container --- */}
                <div
                    ref={scrollRef}
                    className={`
                        flex gap-8 lg:gap-10 overflow-x-auto 
                        scrollbar-hide pb-16 pt-4 px-6 lg:px-2
                        cursor-grab ${isDragging ? 'cursor-grabbing select-none' : ''}
                    `}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleStopDrag}
                    onMouseUp={handleStopDrag}
                    onMouseMove={handleMouseMove}
                >
                    {items.map((item) => (
                        <div
                            key={item.id}
                            className="relative shrink-0 w-[85vw] sm:w-[60vw] lg:w-[40vw] pointer-events-none"
                        >
                            <div className="
                                pointer-events-auto h-full bg-white dark:bg-neutral-900 
                                border-4 border-primary-500 rounded-[2.5rem] 
                                p-8 lg:p-12 shadow-[0_20px_50px_rgba(0,51,102,0.15)] 
                                hover:shadow-[0_30px_70px_rgba(242,145,27,0.2)] 
                                hover:-translate-y-3 transition-all duration-500 ease-out 
                                flex flex-col justify-between group
                            ">
                                <ItemCard
                                    item={item}
                                    dir={dir}
                                    readMoreLabel={uiContent?.readMore}
                                />
                            </div>
                        </div>
                    ))}
                    <div className="w-1 shrink-0 lg:hidden"></div>
                </div>

                {/* --- Progress Bar --- */}
                <div className="px-6 lg:px-2 mt-4">
                    <div className="w-full h-2 bg-primary-100 dark:bg-neutral-800 rounded-full overflow-hidden relative">
                        <div
                            className="h-full bg-secondary-500 absolute top-0 rounded-full transition-all duration-150 ease-out"
                            style={{
                                width: `${barWidth}%`,
                                [dir === 'rtl' ? 'right' : 'left']: `${barOffset}%`,
                            }}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

/**
 * Internal Card Component
 */
const ItemCard = ({ item, dir, readMoreLabel }) => {
    return (
        <div className="flex flex-col h-full w-full relative z-10">
            <div className="flex flex-col items-start gap-8 mb-8 w-full">
                {item.image && (
                    <div className="shrink-0 w-32 h-32 mx-auto flex items-center justify-center">
                        <ImageComponent
                            src={item.image}
                            alt={item.title || 'Item Icon'}
                            className="w-full h-full object-contain drop-shadow-md"
                        />
                    </div>
                )}
                <h3 className="text-4xl md:text-5xl font-black er text-black dark:text-white leading-[1.05]">
                    {item.title}
                </h3>
            </div>

            <div className="flex-grow space-y-5 mb-10">
                {(item.paragraphs || []).map((p, idx) => (
                    <p key={idx} className="text-xl text-gray-600 dark:text-gray-400 font-light leading-relaxed">
                        {p}
                    </p>
                ))}
            </div>

            {(item.link || item.ctaHref) && (
                <div className="mt-auto pt-8 border-t border-gray-200 dark:border-neutral-800 w-full">
                    <a
                        href={item.link || item.ctaHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-3 text-lg font-bold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                    >
                        <span>{item.ctaText || readMoreLabel}</span>
                        <svg
                            className={`w-6 h-6 transition-transform duration-300 ${dir === 'rtl' ? 'rotate-180 group-hover:-translate-x-2' : 'group-hover:translate-x-2'}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2.5}
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                    </a>
                </div>
            )}
        </div>
    );
};

export default TypesSection;